import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useChatContext } from '../context/ChatContext';
import { 
  sendMessage, 
  setChatConfig, 
  toggleChat, 
  clearError,
  logoutChat,
  clearChatManually
} from '../reducers/chat.reducer';

export const useChat = (config = {}) => {
  const { state, dispatch } = useChatContext();
  const configRef = useRef(config);
  
  // Actualizar ref cuando cambia la config
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Memoizar la configuración para evitar cambios innecesarios
  const memoizedConfig = useMemo(() => ({
    mode: config.mode,
    userId: config.userId,
    endpoint: config.endpoint,
    theme: config.theme
  }), [config.mode, config.userId, config.endpoint, config.theme]);

  // Configurar chat al montar el componente
  useEffect(() => {
    if (Object.keys(memoizedConfig).length > 0 && memoizedConfig.userId) {
      dispatch(setChatConfig(memoizedConfig));
    }
  }, [memoizedConfig, dispatch]);

  // Función para enviar mensaje
  const handleSendMessage = useCallback(async (message) => {
    if (!message.trim()) return;
    
    try {
      const currentConfig = configRef.current;
      const messageState = {
        userId: state.userId,
        mode: state.mode,
        endpoint: state.chatConfig?.endpoint || currentConfig.endpoint || 'https://www.ciacosinte.site/cia2_google/JSON/CriterIA/chat_client.php'
      };
      await sendMessage(message, dispatch, messageState)();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [dispatch, state.userId, state.mode, state.chatConfig?.endpoint]);

  // Función para alternar chat
  const handleToggleChat = useCallback(() => {
    dispatch(toggleChat());
  }, [dispatch]);

  // Función para limpiar errores
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Función para configurar usuario
  const handleSetUser = useCallback((userId) => {
    dispatch(setChatConfig({ userId }));
  }, [dispatch]);

  // Función para limpiar chat en logout
  const handleLogout = useCallback(() => {
    dispatch(logoutChat());
  }, [dispatch]);

  // Función para limpiar chat manualmente
  const handleClearChat = useCallback(() => {
    dispatch(clearChatManually());
  }, [dispatch]);

  return {
    // Estado
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    isOpen: state.isOpen,
    mode: state.mode,
    userId: state.userId,
    unreadCount: state.unreadCount,
    
    // Acciones
    sendMessage: handleSendMessage,
    toggleChat: handleToggleChat,
    clearError: handleClearError,
    setUser: handleSetUser,
    logout: handleLogout,
    clearChat: handleClearChat,
    
    // Configuración
    config: state.chatConfig
  };
};
